import { useState } from 'react';
import {
    Box, Button, FormControl, FormLabel, Input, VStack, Heading, Text,
    useToast, Container, Alert, AlertIcon, Card, CardBody, Step, StepDescription,
    StepIcon, StepIndicator, StepNumber, StepSeparator, StepStatus, StepTitle,
    Stepper, useSteps
} from '@chakra-ui/react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';

const ActivationPage = () => {
    const steps = [
        { title: '身分驗證', description: '驗證家長與孩子資訊' },
        { title: '設定密碼', description: '建立您的登入密碼' },
        { title: '完成', description: '啟用成功' },
    ];

    const { activeStep, setActiveStep } = useSteps({
        index: 0,
        count: steps.length,
    });

    const [phone, setPhone] = useState('');
    const [studentName, setStudentName] = useState('');
    const [tempToken, setTempToken] = useState(null);
    const [institutionName, setInstitutionName] = useState('');
    const [verifiedStudentName, setVerifiedStudentName] = useState('');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Step 1: Verify Parent Identity
    const handleVerify = async () => {
        if (!phone || !studentName) {
            toast({ title: "請輸入手機號碼與孩子姓名", status: "warning" });
            return;
        }
        setLoading(true);
        try {
            const res = await authAPI.verifyActivation(phone, studentName);
            if (res.data.valid) {
                setTempToken(res.data.temp_token);
                setInstitutionName(res.data.institution_name);
                setVerifiedStudentName(res.data.student_name);
                setActiveStep(1);
                // [Phase 1 Upgrade] Smart Sibling Alert
                if (res.data.related_students && res.data.related_students.length > 0) {
                    const siblings = res.data.related_students.join(", ");
                    setTimeout(() => {
                        alert(`【系統提示】\n偵測到您還有其他子女就讀本校：\n${siblings}\n\n系統已確認完成綁定！`);
                    }, 100);
                } else if (res.data.merged_students && res.data.merged_students.length > 0) {
                    const siblings = res.data.merged_students.join(", ");
                    setTimeout(() => {
                        alert(`【系統提示】\n偵測到您還有其他子女就讀本校：\n${siblings}\n\n系統已自動為您完成綁定！`);
                    }, 100);
                }

                toast({ title: "驗證成功", status: "success" });
            }
        } catch (error) {
            console.error(error);
            toast({
                title: "驗證失敗",
                description: error.response?.data?.detail || "無法確認您的身分，請聯繫補習班。",
                status: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Set Password
    const handleSetPassword = async () => {
        if (!password || !confirmPassword) {
            toast({ title: "請輸入密碼", status: "warning" });
            return;
        }
        if (password !== confirmPassword) {
            toast({ title: "兩次密碼輸入不一致", status: "error" });
            return;
        }
        if (password.length < 8) {
            toast({ title: "密碼長度至少需 8 碼", status: "warning" });
            return;
        }

        setLoading(true);
        try {
            await authAPI.completeActivation(tempToken, password);
            setActiveStep(2);
            toast({ title: "帳號啟用成功", status: "success" });
        } catch (error) {
            console.error(error);
            toast({
                title: "啟用失敗",
                description: error.response?.data?.detail || "請重試或聯繫補習班。",
                status: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxW="lg" py={12}>
            <VStack spacing={8}>
                <Box textAlign="center">
                    <Heading size="xl" color="brand.600" mb={2}>家長帳號啟用</Heading>
                    <Text color="gray.500">首次使用？請先驗證身分並設定密碼</Text>
                </Box>

                <Stepper index={activeStep} w="100%" colorScheme="blue">
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepIndicator>
                                <StepStatus
                                    complete={<StepIcon />}
                                    incomplete={<StepNumber />}
                                    active={<StepNumber />}
                                />
                            </StepIndicator>
                            <Box flexShrink='0' display={{ base: 'none', md: 'block' }}>
                                <StepTitle>{step.title}</StepTitle>
                                <StepDescription>{step.description}</StepDescription>
                            </Box>
                            <StepSeparator />
                        </Step>
                    ))}
                </Stepper>

                <Card w="100%" shadow="lg" borderRadius="xl">
                    <CardBody p={8}>
                        {activeStep === 0 && (
                            <Box key="step-container-0">
                                <VStack spacing={6}>
                                    <Text>請輸入您留給補習班的手機號碼，以及孩子的姓名。</Text>
                                    <FormControl isRequired>
                                        <FormLabel>家長手機號碼</FormLabel>
                                        <Input
                                            placeholder="0912345678"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            type="tel"
                                        />
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>孩子姓名</FormLabel>
                                        <Input
                                            placeholder="王小明"
                                            value={studentName}
                                            onChange={(e) => setStudentName(e.target.value)}
                                        />
                                    </FormControl>
                                    <Button
                                        colorScheme="blue"
                                        width="100%"
                                        size="lg"
                                        onClick={handleVerify}
                                        isLoading={loading}
                                    >
                                        下一步：驗證身分
                                    </Button>
                                    <Text fontSize="sm" color="gray.500">
                                        已有帳號？ <Link to="/login" style={{ color: '#3182ce' }}>直接登入</Link>
                                    </Text>
                                </VStack>
                            </Box>
                        )}

                        {activeStep === 1 && (
                            <Box key="step-container-1">
                                <VStack spacing={6}>
                                    <Alert status="success" borderRadius="md">
                                        <AlertIcon />
                                        <Box>
                                            <Text fontWeight="bold">身分驗證成功！</Text>
                                            <Text fontSize="sm">
                                                您是 <Text as="span" fontWeight="bold" color="blue.600">{verifiedStudentName}</Text> 的家長。<br />
                                                就讀機構：{institutionName}
                                            </Text>
                                        </Box>
                                    </Alert>

                                    <Text fontWeight="bold">請設定您的登入密碼：</Text>

                                    <FormControl isRequired>
                                        <FormLabel>設定密碼 (至少8碼)</FormLabel>
                                        <Input
                                            type="password"
                                            placeholder="請輸入新密碼"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl isRequired>
                                        <FormLabel>確認密碼</FormLabel>
                                        <Input
                                            type="password"
                                            placeholder="請再次輸入密碼"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />
                                    </FormControl>
                                    <Button
                                        colorScheme="green"
                                        width="100%"
                                        size="lg"
                                        onClick={handleSetPassword}
                                        isLoading={loading}
                                    >
                                        啟用帳號
                                    </Button>
                                </VStack>
                            </Box>
                        )}

                        {activeStep === 2 && (
                            <Box key="step-container-2">
                                <VStack spacing={6} textAlign="center" py={4}>
                                    <Text fontSize="4xl">🎉</Text>
                                    <Heading size="md">帳號啟用成功！</Heading>
                                    <Text color="gray.600">
                                        您現在可以使用手機號碼與剛設定的密碼登入系統了。
                                    </Text>
                                    <Button
                                        colorScheme="blue"
                                        width="100%"
                                        size="lg"
                                        onClick={() => navigate('/login')}
                                    >
                                        前往登入頁面
                                    </Button>
                                </VStack>
                            </Box>
                        )}
                    </CardBody>
                </Card>
            </VStack>
        </Container>
    );
};

export default ActivationPage;
